/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

/**
 * useStoryInfo - Story and Tab Information Provider
 *
 * Provides enriched story and tab information, including block metadata
 * and function arguments from live editor or cached content.
 */

import { useTabManagerContext } from '../context/TabManagerContext';
import { findTabById } from '../helpers/codeEditorHelpers';
import { StepConverter } from '../managers/StepConverter';

import { CachedTab } from './types';
import { useStepEnricher } from './useStepEnricher';

import { Step as UIStep } from '@/pages/projects/LiveAgentTypes';

/**
 * Story information interface
 */
export interface StoryInfo {
    steps: UIStep[];
    blockDefArgs?: string[];
    blockInputArgs?: string[];
    blockHasReturnValue?: boolean;
}

/**
 * Tab information interface
 */
export interface TabInfo {
    steps: UIStep[];
    blockDefArgs?: string[];
    blockInputArgs?: string[];
    blockHasReturnValue?: boolean;
}

export const useStoryInfo = () => {
    const { editorRef, activeTabIdRef, tabsRef, astParser } = useTabManagerContext();

    // Use the step enricher hook
    const { enrichBlockSteps } = useStepEnricher();

    /**
     * Get complete story information with enriched block steps
     * Uses useStepEnricher hook - reduced from 2 parameters to 0
     * @param getCachedTab - Function to get cached tab data
     * @returns Complete story information
     */
    const getStoryInfo = (getCachedTab: (key: string) => CachedTab | undefined): StoryInfo => {
        let storySteps: UIStep[] = [];
        let blockDefArgs: string[] | undefined;
        let blockInputArgs: string[] | undefined;
        let blockHasReturnValue: boolean | undefined;

        if (activeTabIdRef.current === 'main') {
            // For main tab, get steps from live editor
            const activeTab = findTabById(tabsRef.current, activeTabIdRef.current);
            if (activeTab?.metadataManager && editorRef.current) {
                try {
                    const code = editorRef.current.getValue();
                    storySteps = StepConverter.codeToSteps(
                        code,
                        editorRef.current,
                        astParser,
                        activeTab.metadataManager,
                    );

                    // Extract block function info if main tab contains a block function
                    const blockFunctionInfo = astParser.extractBlockFunctionInfo(code);
                    if (blockFunctionInfo) {
                        blockDefArgs = blockFunctionInfo.args;
                        blockHasReturnValue = blockFunctionInfo.hasReturnValue;
                    }
                } catch (e) {
                    storySteps = [];
                }
            }

            // Enrich steps with block information using the hook
            storySteps = enrichBlockSteps(storySteps);
        } else {
            // For block tabs, get from cache
            const cachedMainTab = getCachedTab('main');
            if (cachedMainTab) {
                storySteps = enrichBlockSteps(cachedMainTab.steps || []);

                // Extract block function info from cached main tab code
                if (cachedMainTab.code) {
                    const blockFunctionInfo = astParser.extractBlockFunctionInfo(cachedMainTab.code);
                    if (blockFunctionInfo) {
                        blockDefArgs = blockFunctionInfo.args;
                        blockHasReturnValue = blockFunctionInfo.hasReturnValue;
                    }
                }
            }
        }

        return { steps: storySteps, blockDefArgs, blockInputArgs, blockHasReturnValue };
    };

    /**
     * Get information about a specific tab
     * Uses useStepEnricher hook - reduced from 3 parameters to 2
     * @param tabKey - Key of the tab to get info for
     * @param getCachedTab - Function to get cached tab data
     * @returns Tab information
     */
    const getTabInfo = (tabKey: string, getCachedTab: (key: string) => CachedTab | undefined): TabInfo => {
        let storySteps: UIStep[] = [];
        let blockDefArgs: string[] | undefined;
        let blockInputArgs: string[] | undefined;
        let blockHasReturnValue: boolean | undefined;

        if (tabKey === 'main') {
            // For main tab
            if (activeTabIdRef.current === 'main') {
                // Get from live editor if currently active
                const activeTab = findTabById(tabsRef.current, activeTabIdRef.current);
                if (activeTab?.metadataManager && editorRef.current) {
                    try {
                        const code = editorRef.current.getValue();
                        storySteps = StepConverter.codeToSteps(
                            code,
                            editorRef.current,
                            astParser,
                            activeTab.metadataManager,
                        );

                        // Extract block function info if main tab contains a block function
                        const blockFunctionInfo = astParser.extractBlockFunctionInfo(code);
                        if (blockFunctionInfo) {
                            blockDefArgs = blockFunctionInfo.args;
                            blockHasReturnValue = blockFunctionInfo.hasReturnValue;
                        }
                    } catch (e) {
                        storySteps = [];
                    }
                }

                // Enrich steps with block information using the hook
                storySteps = enrichBlockSteps(storySteps);
            } else {
                // Get from cache
                const cachedTab = getCachedTab('main');
                if (cachedTab) {
                    storySteps = enrichBlockSteps(cachedTab.steps || []);

                    // Extract block function info from cached main tab code
                    if (cachedTab.code) {
                        const blockFunctionInfo = astParser.extractBlockFunctionInfo(cachedTab.code);
                        if (blockFunctionInfo) {
                            blockDefArgs = blockFunctionInfo.args;
                            blockHasReturnValue = blockFunctionInfo.hasReturnValue;
                        }
                    }
                }
            }
        } else {
            // For block tabs
            const blockTabId = `block-${tabKey}`;
            if (activeTabIdRef.current === blockTabId) {
                // Get from live editor if currently active
                const activeTab = findTabById(tabsRef.current, activeTabIdRef.current);
                if (activeTab?.metadataManager && editorRef.current) {
                    try {
                        const code = editorRef.current.getValue();
                        storySteps = StepConverter.codeToSteps(
                            code,
                            editorRef.current,
                            astParser,
                            activeTab.metadataManager,
                        );

                        // Extract block function info for block tabs
                        const blockFunctionInfo = astParser.extractBlockFunctionInfo(code);
                        if (blockFunctionInfo) {
                            blockDefArgs = blockFunctionInfo.args;
                            blockHasReturnValue = blockFunctionInfo.hasReturnValue;
                        }
                    } catch (e) {
                        storySteps = [];
                    }
                }
            } else {
                // Get from cache
                const cachedTab = getCachedTab(tabKey);
                if (cachedTab) {
                    storySteps = enrichBlockSteps(cachedTab.steps || []);

                    // Extract block function info from cached code
                    if (cachedTab.code) {
                        const blockFunctionInfo = astParser.extractBlockFunctionInfo(cachedTab.code);
                        if (blockFunctionInfo) {
                            blockDefArgs = blockFunctionInfo.args;
                            blockHasReturnValue = blockFunctionInfo.hasReturnValue;
                        }
                    }
                }
            }
        }

        return { steps: storySteps, blockDefArgs, blockInputArgs, blockHasReturnValue };
    };

    return {
        getStoryInfo,
        getTabInfo,
    };
};
